import { Table, Card, Title } from '@bancobarigui/barihaus';
import { Switch, InputNumber } from 'antd';
import { DadosValorTerreno, SwitchesValorTerreno, DadosAreaConstruida } from '@/features/laudo/types/laudo-wizard.types';
import { useMemo, useEffect } from 'react';
import { calcularLimitesHomogeneizacao, valorDentroDoIntervalo } from './CardHomogeneizacao';
import { ReviewFieldWrapper } from '../../ReviewFieldWrapper';
 '../ReviewFieldWrapper';

interface TabelaValorTerrenoProps {
  dados: DadosValorTerreno[];
  switches: SwitchesValorTerreno;
  dadosAreaConstruida: DadosAreaConstruida[];
  onUpdateDados: (dados: DadosValorTerreno[]) => void;
  onUpdateSwitches: (switches: SwitchesValorTerreno) => void;
  disabled?: boolean;
  displayIdMap?: Record<string, string>;
}

const calcularValorUnitario = (valorTerreno: number, areaTerreno: number): number => {
  if (areaTerreno === 0) return 0;
  return valorTerreno / areaTerreno;
};

const calcularFAT = (
  item: DadosValorTerreno,
  switches: SwitchesValorTerreno
): number => {
  let somaDesvios = 0;

  somaDesvios += (item.fatorTopografia || 1.0) - 1.0;
  if (switches.area) somaDesvios += (item.fatorArea || 1.0) - 1.0;
  if (switches.testada) somaDesvios += (item.fatorTestada || 1.0) - 1.0;
  if (switches.vegetacao) somaDesvios += (item.fatorVegetacao || 1.0) - 1.0;
  if (switches.fatorExtraI) somaDesvios += (item.fatorExtraI || 1.0) - 1.0;
  if (switches.fatorExtraII) somaDesvios += (item.fatorExtraII || 1.0) - 1.0;
  if (switches.fatorExtraIII) somaDesvios += (item.fatorExtraIII || 1.0) - 1.0;

  return 1.0 + somaDesvios;
};

const calcularValorUnitarioFinal = (
  valorUnitario: number,
  indiceFiscal: number,
  fatorOferta: number,
  fat: number
): number => {
  return valorUnitario * indiceFiscal * fatorOferta * fat;
};

const formatarMoeda = (valor: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor);
};

const formatarFator = (valor: number): string => {
  return valor.toFixed(2);
};

export function TabelaValorTerreno({
  dados,
  switches,
  dadosAreaConstruida,
  onUpdateDados,
  onUpdateSwitches,
  disabled,
  displayIdMap,
}: TabelaValorTerrenoProps) {

  useEffect(() => {
    const novosDados = dados.map((item) => {
      const construcao = dadosAreaConstruida.find(
        (ac) => ac.identificacao === item.amostra
      );

      const valorTerreno = item.valorTotalAmostra - (construcao?.valorFinal || 0);

      return {
        ...item,
        valorTerreno,
      };
    });

    const precisaAtualizar = novosDados.some((itemNovo, index) => {
      const itemOriginal = dados[index];
      if (!itemOriginal) return false;
      return Math.abs(itemNovo.valorTerreno - itemOriginal.valorTerreno) > 0.01;
    });

    if (precisaAtualizar) {
      onUpdateDados(novosDados);
    }
  }, [dadosAreaConstruida, dados, onUpdateDados]);

  const dadosCalculados = useMemo(() => {
    return dados.map((item) => {
      const valorUnitario = calcularValorUnitario(item.valorTerreno, item.areaTerreno);
      const fat = calcularFAT(item, switches);
      const valorUnitarioFinal = calcularValorUnitarioFinal(
        valorUnitario,
        item.indiceFiscal,
        item.fatorOferta,
        fat
      );

      return {
        ...item,
        valorUnitario,
        fat,
        valorUnitarioFinal,
      };
    });
  }, [dados, switches]);

  useEffect(() => {
    const precisaAtualizar = dadosCalculados.some((itemCalculado, index) => {
      const itemOriginal = dados[index];
      if (!itemOriginal) return false;

      return (
        Math.abs(itemCalculado.valorUnitario - (itemOriginal.valorUnitario || 0)) > 0.01 ||
        Math.abs(itemCalculado.fat - (itemOriginal.fat || 0)) > 0.0001 ||
        Math.abs(itemCalculado.valorUnitarioFinal - (itemOriginal.valorUnitarioFinal || 0)) > 0.01
      );
    });

    if (precisaAtualizar) {
      onUpdateDados(dadosCalculados);
    }
  }, [dadosCalculados, dados, onUpdateDados]);

  const dadosFiltrados = useMemo(() => {
    return dadosCalculados.filter(item =>
      item.amostra !== 'Avaliando' && item.tipoImovel !== 'Apartamento'
    );
  }, [dadosCalculados]);

  const limitesHomogeneizacao = useMemo(() => {
    return calcularLimitesHomogeneizacao(dadosFiltrados);
  }, [dadosFiltrados]);

  const mediaValorUnitario = useMemo(() => {
    if (dadosFiltrados.length === 0) return 0;
    const soma = dadosFiltrados.reduce((acc, item) => acc + item.valorUnitarioFinal, 0);
    return soma / dadosFiltrados.length;
  }, [dadosFiltrados]);

  const handleInputChange = (
    index: number,
    field: keyof DadosValorTerreno,
    value: number | string
  ) => {
    const novosDados = [...dados];
    novosDados[index] = {
      ...novosDados[index],
      [field]: value,
    } as DadosValorTerreno;
    onUpdateDados(novosDados);
  };

  // Preserva o 0 digitado (não vira 1) e ignora o campo vazio (null) para o
  // usuário poder apagar tudo e redigitar sem o valor voltar para o default.
  const handleFatorChange = (index: number, field: keyof DadosValorTerreno) =>
    (newValue: number | null) => {
      if (newValue === null) return;
      handleInputChange(index, field, newValue);
    };

  const handleSwitchChange = (field: keyof SwitchesValorTerreno, value: boolean) => {
    onUpdateSwitches({
      ...switches,
      [field]: value,
    });
  };

  const colunasBase: any[] = [
    {
      title: 'Amostra',
      dataIndex: 'amostra',
      key: 'amostra',
      width: 90,
      fixed: 'left' as const,
      render: (text: string) => <strong>{displayIdMap?.[text] || text}</strong>,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipoImovel',
      key: 'tipoImovel',
      width: 100,
      fixed: 'left' as const,
      render: (tipo: string) => (
        <span style={{ 
          fontSize: '12px',
          color: tipo === 'Terreno' ? '#52c41a' : tipo === 'Casa' ? '#1890ff' : '#722ed1'
        }}>
          {tipo}
        </span>
      ),
    },
    {
      title: 'Terreno (m²)',
      dataIndex: 'areaTerreno',
      key: 'areaTerreno',
      width: 130,
      render: (value: number) => {
        const area = Number(value || 0);
        return (
          <div
            style={{
              textAlign: 'center',
              fontWeight: 500,
              color: '#666',
              backgroundColor: '#fafafa',
              padding: '4px 8px',
              borderRadius: 4,
            }}
            title="Campo automático - vem dos dados da amostra"
          >
            {area.toFixed(2)} m²
          </div>
        );
      },
    },
    {
      title: 'Padrão',
      dataIndex: 'padrao',
      key: 'padrao',
      width: 120,
      render: (value: string) => (
        <div
          style={{
            textAlign: 'center',
            fontWeight: 500,
            color: '#666',
            backgroundColor: '#fafafa',
            padding: '4px 8px',
            borderRadius: 4,
          }}
          title="Campo automático - vem dos dados da amostra"
        >
          {value}
        </div>
      ),
    },
    {
      title: 'Topografia',
      dataIndex: 'topografia',
      key: 'topografia',
      width: 130,
      render: (value: string) => (
        <div
          style={{
            textAlign: 'center',
            fontWeight: 500,
            color: '#666',
            backgroundColor: '#fafafa',
            padding: '4px 8px',
            borderRadius: 4,
          }}
          title="Campo automático - vem dos dados da amostra"
        >
          {value}
        </div>
      ),
    },
    {
      title: 'Valor Terreno (R$)',
      dataIndex: 'valorTerreno',
      key: 'valorTerreno',
      width: 160,
      render: (value: number) => {
        const valorTerreno = typeof value === 'number' ? value : 0;
        return (
          <div
            style={{
              textAlign: 'right',
              fontWeight: 600,
              color: '#1890ff',
              backgroundColor: '#fafafa',
              padding: '4px 8px',
              borderRadius: 4,
            }}
            title="Campo calculado automaticamente: Valor Total - Valor da Construção"
          >
            {formatarMoeda(valorTerreno)}
          </div>
        );
      },
    },
    {
      title: 'Valor Unit. (R$/m²)',
      dataIndex: 'valorUnitario',
      key: 'valorUnitario',
      width: 150,
      render: (_: any, record: DadosValorTerreno) => {
        return (
          <div style={{ textAlign: 'right', fontWeight: 500, color: '#595959' }}>
            {formatarMoeda(record.valorUnitario)}
          </div>
        );
      },
    },
    {
      title: 'Índice Fiscal',
      dataIndex: 'indiceFiscal',
      key: 'indiceFiscal',
      width: 130,
      render: (value: number, record: DadosValorTerreno) => {
        const indexOriginal = dados.findIndex(d => d.amostra === record.amostra);
        return (
          <InputNumber
            value={value}
            onChange={handleFatorChange(indexOriginal, 'indiceFiscal')}
            style={{ width: '100%' }}
            step={0.01}
            precision={2}
            decimalSeparator=","
            disabled={disabled ?? false}
          />
        );
      },
    },
    {
      title: 'F. Oferta',
      dataIndex: 'fatorOferta',
      key: 'fatorOferta',
      width: 110,
      render: (value: number, record: DadosValorTerreno) => {
        const indexOriginal = dados.findIndex(d => d.amostra === record.amostra);
        return (
          <InputNumber
            value={value}
            onChange={handleFatorChange(indexOriginal, 'fatorOferta')}
            style={{ width: '100%' }}
            step={0.01}
            precision={2}
            decimalSeparator=","
            disabled={disabled ?? false}
          />
        );
      },
    },
    {
      title: 'F. Topografia',
      dataIndex: 'fatorTopografia',
      key: 'fatorTopografia',
      width: 130,
      render: (value: number, record: DadosValorTerreno) => {
        const indexOriginal = dados.findIndex(d => d.amostra === record.amostra);
        return (
          <InputNumber
            value={value}
            onChange={handleFatorChange(indexOriginal, 'fatorTopografia')}
            style={{ width: '100%' }}
            step={0.01}
            precision={2}
            decimalSeparator=","
            disabled={disabled ?? false}
          />
        );
      },
    },
  ];

  const colunasCondicionais: any[] = [];

  if (switches.area) {
    colunasCondicionais.push({
      title: 'F. Área',
      dataIndex: 'fatorArea',
      key: 'fatorArea',
      width: 110,
      render: (value: number, record: DadosValorTerreno) => {
        const indexOriginal = dados.findIndex(d => d.amostra === record.amostra);
        return (
          <InputNumber
            value={value ?? 1.0}
            onChange={handleFatorChange(indexOriginal, 'fatorArea')}
            style={{ width: '100%' }}
            step={0.01}
            precision={2}
            decimalSeparator=","
            disabled={disabled ?? false}
          />
        );
      },
    });
  }

  if (switches.testada) {
    colunasCondicionais.push({
      title: 'F. Testada',
      dataIndex: 'fatorTestada',
      key: 'fatorTestada',
      width: 110,
      render: (value: number, record: DadosValorTerreno) => {
        const indexOriginal = dados.findIndex(d => d.amostra === record.amostra);
        return (
          <InputNumber
            value={value ?? 1.0}
            onChange={handleFatorChange(indexOriginal, 'fatorTestada')}
            style={{ width: '100%' }}
            step={0.01}
            precision={2}
            decimalSeparator=","
            disabled={disabled ?? false}
          />
        );
      },
    });
  }

  if (switches.vegetacao) {
    colunasCondicionais.push({
      title: 'F. Vegetação',
      dataIndex: 'fatorVegetacao',
      key: 'fatorVegetacao',
      width: 120,
      render: (value: number, record: DadosValorTerreno) => {
        const indexOriginal = dados.findIndex(d => d.amostra === record.amostra);
        return (
          <InputNumber
            value={value ?? 1.0}
            onChange={handleFatorChange(indexOriginal, 'fatorVegetacao')}
            style={{ width: '100%' }}
            step={0.01}
            precision={2}
            decimalSeparator=","
            disabled={disabled ?? false}
          />
        );
      },
    });
  }

  if (switches.fatorExtraI) {
    colunasCondicionais.push({
      title: 'F. Extra I',
      dataIndex: 'fatorExtraI',
      key: 'fatorExtraI',
      width: 110,
      render: (value: number, record: DadosValorTerreno) => {
        const indexOriginal = dados.findIndex(d => d.amostra === record.amostra);
        return (
          <InputNumber
            value={value ?? 1.0}
            onChange={handleFatorChange(indexOriginal, 'fatorExtraI')}
            style={{ width: '100%' }}
            step={0.01}
            precision={2}
            decimalSeparator=","
            disabled={disabled ?? false}
          />
        );
      },
    });
  }

  if (switches.fatorExtraII) {
    colunasCondicionais.push({
      title: 'F. Extra II',
      dataIndex: 'fatorExtraII',
      key: 'fatorExtraII',
      width: 110,
      render: (value: number, record: DadosValorTerreno) => {
        const indexOriginal = dados.findIndex(d => d.amostra === record.amostra);
        return (
          <InputNumber
            value={value ?? 1.0}
            onChange={handleFatorChange(indexOriginal, 'fatorExtraII')}
            style={{ width: '100%' }}
            step={0.01}
            precision={2}
            decimalSeparator=","
            disabled={disabled ?? false}
          />
        );
      },
    });
  }

  if (switches.fatorExtraIII) {
    colunasCondicionais.push({
      title: 'F. Extra III',
      dataIndex: 'fatorExtraIII',
      key: 'fatorExtraIII',
      width: 120,
      render: (value: number, record: DadosValorTerreno) => {
        const indexOriginal = dados.findIndex(d => d.amostra === record.amostra);
        return (
          <InputNumber
            value={value ?? 1.0}
            onChange={handleFatorChange(indexOriginal, 'fatorExtraIII')}
            style={{ width: '100%' }}
            step={0.01}
            precision={2}
            decimalSeparator=","
            disabled={disabled ?? false}
          />
        );
      },
    });
  }

  const colunasFinais: any[] = [
    {
      title: 'FAT',
      dataIndex: 'fat',
      key: 'fat',
      width: 100,
      render: (_: any, record: DadosValorTerreno) => {
        return (
          <div style={{ textAlign: 'right', fontWeight: 600, color: '#fa8c16' }}>
            {formatarFator(record.fat)}
          </div>
        );
      },
    },
    {
      title: 'Valor Unit. Final (R$/m²)',
      dataIndex: 'valorUnitarioFinal',
      key: 'valorUnitarioFinal',
      width: 180,
      render: (_: any, record: DadosValorTerreno) => {
        return (
          <div style={{ textAlign: 'right', fontWeight: 700, color: '#52c41a', fontSize: 14 }}>
            {formatarMoeda(record.valorUnitarioFinal)}
          </div>
        );
      },
    },
  ];

  const todasColunas = [...colunasBase, ...colunasCondicionais, ...colunasFinais];

  return (
    <div style={{ marginTop: 40 }}>

      <div style={{ marginBottom: 16 }}>
        <ReviewFieldWrapper
          fieldPath="amostras.tabelaValorTerreno"
          fieldLabel="Tabela de Valor do Terreno"
          stepName="Metodologia"
          linkInline={true}
        >
          <Title level={4}>Cálculo de Valor do Terreno</Title>
        </ReviewFieldWrapper>
      </div>
        <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
          Fatores de Ajuste
        </div>
        <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch
              checked={switches.area}
              onChange={(checked) => handleSwitchChange('area', checked)}
              size="small"
              disabled={disabled ?? false}
            />
            <span style={{ fontSize: 13 }}>Área</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch
              checked={switches.testada}
              onChange={(checked) => handleSwitchChange('testada', checked)}
              size="small"
              disabled={disabled ?? false}
            />
            <span style={{ fontSize: 13 }}>Testada</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch
              checked={switches.vegetacao}
              onChange={(checked) => handleSwitchChange('vegetacao', checked)}
              size="small"
              disabled={disabled ?? false}
            />
            <span style={{ fontSize: 13 }}>Vegetação</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch
              checked={switches.fatorExtraI}
              onChange={(checked) => handleSwitchChange('fatorExtraI', checked)}
              size="small"
              disabled={disabled ?? false}
            />
            <span style={{ fontSize: 13 }}>Fator Extra I</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch
              checked={switches.fatorExtraII}
              onChange={(checked) => handleSwitchChange('fatorExtraII', checked)}
              size="small"
              disabled={disabled ?? false}
            />
            <span style={{ fontSize: 13 }}>Fator Extra II</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Switch
              checked={switches.fatorExtraIII}
              onChange={(checked) => handleSwitchChange('fatorExtraIII', checked)}
              size="small"
              disabled={disabled ?? false}
            />
            <span style={{ fontSize: 13 }}>Fator Extra III</span>
          </div>
        </div>

      {}
      <Table
        dataSource={dadosFiltrados}
        columns={todasColunas}
        rowKey="amostra"
        pagination={false}
        scroll={{ x: 1600 }}
        bordered
        size="small"
        rowClassName={(record) => {
          const foraDoIntervalo = !valorDentroDoIntervalo(
            record.valorUnitarioFinal,
            limitesHomogeneizacao.limiteInferior,
            limitesHomogeneizacao.limiteSuperior
          );

          if (foraDoIntervalo) {
            return 'row-fora-intervalo';
          }

          return '';
        }}
      />

      {}
      <div
        style={{
          padding: "16px 0px",

        }}
      >
        <div>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>
            Média Final - Valor Unitário (R$/m²)
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1890ff' }}>
            {formatarMoeda(mediaValorUnitario)}
          </div>
        </div>
      </div>

      {}
      <style>
        {`
          .row-fora-intervalo {
            background-color: #fff1f0 !important;
            border-left: 3px solid #ff4d4f !important;
          }
          .row-fora-intervalo:hover {
            background-color: #ffccc7 !important;
          }
          .row-fora-intervalo td {
            color: #cf1322 !important;
          }
        `}
      </style>
    </div>
  );
}
